// src/features/operational/components/subject/SubjectResources.js
import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Button, 
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Menu
} from '@mui/material';
import {
  Folder as FolderIcon,
  Description as DescriptionIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  MenuBook as SubjectFileIcon,
  LocalLibrary as LTSMIcon,
  FileCopy as FormsIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';
import { useAuth } from '../../../../contexts/AuthContext';

const SubjectResources = ({ userId }) => {
  const { userProfile } = useAuth();
  
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resourceType, setResourceType] = useState('subjectFile');
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeResourceId, setActiveResourceId] = useState(null);
  
  // Dialogs
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isAddLinkDialogOpen, setIsAddLinkDialogOpen] = useState(false);
  
  // Form data
  const [newResourceData, setNewResourceData] = useState({
    name: '',
    description: '',
    link: '',
    type: 'link'
  });
  
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Notification
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Mock data
  const mockSubjects = [
    { id: '1', name: 'Mathematics', code: 'MATH101', grade: '10', phase: 'FET', department: 'Mathematics', status: 'Active', description: 'Grade 10 Mathematics curriculum' },
    { id: '2', name: 'Physical Sciences', code: 'PHYS101', grade: '10', phase: 'FET', department: 'Sciences', status: 'Active', description: 'Grade 10 Physical Sciences curriculum' },
    { id: '3', name: 'English Home Language', code: 'ENG101', grade: '8', phase: 'Senior', department: 'Languages', status: 'Active', description: 'Grade 8 English curriculum' },
    { id: '4', name: 'Life Sciences', code: 'LIFE101', grade: '11', phase: 'FET', department: 'Sciences', status: 'Active', description: 'Grade 11 Life Sciences curriculum' },
    { id: '5', name: 'Geography', code: 'GEO101', grade: '9', phase: 'Senior', department: 'Social Sciences', status: 'Active', description: 'Grade 9 Geography curriculum' }
  ];
  
  const mockResources = {
    subjectFile: [
      { id: '1', name: 'CAPS Document', description: 'Official CAPS document for Mathematics', type: 'pdf', url: '#', uploadedAt: '2025-02-15', size: '1.2 MB' },
      { id: '2', name: 'Syllabus Overview', description: 'Term-by-term breakdown of the curriculum', type: 'docx', url: '#', uploadedAt: '2025-03-01', size: '850 KB' }
    ],
    ltsm: [
      { id: '3', name: 'Grade 10 Textbook', description: 'Digital version of the prescribed textbook', type: 'pdf', url: '#', uploadedAt: '2025-02-10', size: '15.6 MB' },
      { id: '4', name: 'Mathematics Formula Sheet', description: 'Comprehensive formula reference for Grade 10', type: 'pdf', url: '#', uploadedAt: '2025-03-05', size: '320 KB' }
    ],
    forms: [
      { id: '5', name: 'Assessment Rubric', description: 'Standardized rubric for mathematics assessments', type: 'docx', url: '#', uploadedAt: '2025-02-20', size: '450 KB' },
      { id: '6', name: 'Khan Academy Resources', description: 'Curated list of Khan Academy videos and exercises', type: 'link', url: 'https://www.khanacademy.org/math', uploadedAt: '2025-03-10' }
    ]
  };
  
  useEffect(() => {
    // Simulating API call
    setTimeout(() => {
      setSubjects(mockSubjects);
      setSelectedSubject(mockSubjects[0]);
      setLoading(false);
    }, 800);
  }, []);
  
  useEffect(() => {
    if (selectedSubject) {
      setResourcesLoading(true);
      // Simulating API call
      setTimeout(() => {
        setResources(mockResources[resourceType] || []);
        setResourcesLoading(false);
      }, 600);
    }
  }, [selectedSubject, resourceType]);
  
  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
  };
  
  const handleResourceTypeChange = (event, newValue) => {
    setResourceType(newValue);
  };
  
  const handleMenuClick = (event, resourceId) => {
    setAnchorEl(event.currentTarget);
    setActiveResourceId(resourceId);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveResourceId(null);
  };
  
  const handleDeleteResource = () => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      setResources(resources.filter(resource => resource.id !== activeResourceId));
      showNotification('Resource deleted successfully', 'success');
    }
    handleMenuClose();
  };
  
  // Upload Dialog
  const handleUploadDialogOpen = () => {
    setSelectedFile(null);
    setIsUploadDialogOpen(true);
  };
  
  const handleUploadDialogClose = () => {
    setIsUploadDialogOpen(false);
    setSelectedFile(null);
  };
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };
  
  const handleUploadResource = () => {
    if (!selectedFile) return;
    
    // Get file extension
    const extension = selectedFile.name.split('.').pop();
    
    // Create new resource
    const newResource = {
      id: `resource_${Date.now()}`,
      name: selectedFile.name.replace(`.${extension}`, ''),
      description: '',
      type: extension,
      url: URL.createObjectURL(selectedFile),
      uploadedAt: new Date().toISOString(),
      size: `${(selectedFile.size / 1024).toFixed(2)} KB`
    };
    
    setResources([newResource, ...resources]);
    showNotification('Resource uploaded successfully', 'success');
    handleUploadDialogClose();
  };
  
  // Add Link Dialog
  const handleAddLinkDialogOpen = () => {
    setNewResourceData({
      name: '',
      description: '',
      link: '',
      type: 'link'
    });
    setIsAddLinkDialogOpen(true);
  };
  
  const handleAddLinkDialogClose = () => {
    setIsAddLinkDialogOpen(false);
  };
  
  const handleNewResourceChange = (e) => {
    const { name, value } = e.target;
    setNewResourceData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleAddLink = () => {
    if (!newResourceData.name || !newResourceData.link) return;
    
    // Create new resource
    const newResource = {
      id: `resource_${Date.now()}`,
      name: newResourceData.name,
      description: newResourceData.description,
      type: 'link',
      url: newResourceData.link,
      uploadedAt: new Date().toISOString()
    };
    
    setResources([newResource, ...resources]);
    showNotification('Resource link added successfully', 'success');
    handleAddLinkDialogClose();
  };
  
  // Notifications
  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };
  
  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
        return <DescriptionIcon sx={{ color: '#f44336' }} />;
      case 'docx':
      case 'doc':
        return <DescriptionIcon sx={{ color: '#2196f3' }} />;
      case 'xlsx':
      case 'xls':
        return <DescriptionIcon sx={{ color: '#4caf50' }} />;
      case 'pptx':
      case 'ppt':
        return <DescriptionIcon sx={{ color: '#ff9800' }} />;
      case 'link':
        return <LinkIcon sx={{ color: THEME_COLORS.primary }} />;
      default:
        return <DescriptionIcon sx={{ color: 'text.secondary' }} />;
    }
  };
  
  const getResourceTypeIcon = (type) => {
    switch (type) {
      case 'subjectFile':
        return <SubjectFileIcon />;
      case 'ltsm':
        return <LTSMIcon />;
      case 'forms':
        return <FormsIcon />;
      default:
        return <DescriptionIcon />;
    }
  };
  
  const filteredResources = resources.filter(resource => {
    return !searchTerm || 
      resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase());
  });
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress sx={{ color: THEME_COLORS.primary }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Bar */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold" color={THEME_COLORS.primary}>
            Subject Resources
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleUploadDialogOpen}
              sx={{
                borderColor: THEME_COLORS.primary,
                color: THEME_COLORS.primary,
                mr: 1,
                '&:hover': {
                  borderColor: '#1e1c4e',
                  backgroundColor: 'rgba(39, 35, 97, 0.04)'
                }
              }}
            >
              Upload File
            </Button>
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={handleAddLinkDialogOpen}
              sx={{
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
            >
              Add Link
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ position: 'relative' }}>
          <SearchIcon
            sx={{
              position: 'absolute',
              left: 1,
              top: '50%',
              transform: 'translateY(-50%)',
              ml: 1,
              color: 'text.secondary'
            }}
          />
          <TextField 
            fullWidth
            placeholder="Search resources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ 
              '& .MuiOutlinedInput-root': {
                paddingLeft: '35px'
              }
            }}
          />
        </Box>
      </Paper>
      
      {/* Use alignItems="stretch" to ensure child items fill the vertical space */}
      <Grid container spacing={2} alignItems="flex-start">

        {/* Resources Panel */}
        <Grid item sx={{ width: { xs: '100%', md: '65%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%', width: '100%' }}>
            <CardContent>
              <Box mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  {selectedSubject ? `${selectedSubject.name} Resources` : 'Subject Resources'}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                  <Chip
                    label={`Grade ${selectedSubject?.grade}`}
                    size="small"
                    sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                  <Chip
                    label={`${selectedSubject?.phase} Phase`}
                    size="small"
                    sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                  />
                </Box>
              </Box>
              
              {selectedSubject && (
                <Tabs
                  value={resourceType}
                  onChange={handleResourceTypeChange}
                  variant="fullWidth"
                  indicatorColor="primary"
                  textColor="primary"
                  sx={{
                    mb: 2,
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600
                    },
                    '& .Mui-selected': {
                      color: "#ED1566"
                    }
                  }}
                >
                  <Tab
                    icon={<SubjectFileIcon />}
                    iconPosition="start"
                    label="Subject File"
                    value="subjectFile"
                  />
                  <Tab
                    icon={<LTSMIcon />}
                    iconPosition="start"
                    label="LTSM"
                    value="ltsm"
                  />
                  <Tab
                    icon={<FormsIcon />}
                    iconPosition="start"
                    label="Forms"
                    value="forms"
                  />
                </Tabs>
              )}
              
              <Divider sx={{ mb: 2 }} />
              
              {!selectedSubject ? (
                <Box py={4} textAlign="center">
                  <Typography color="textSecondary">
                    Please select a subject to view resources
                  </Typography>
                </Box>
              ) : resourcesLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                  <CircularProgress sx={{ color: THEME_COLORS.primary }} />
                </Box>
              ) : filteredResources.length === 0 ? (
                <Box py={4} textAlign="center">
                  <Typography color="textSecondary" mb={2}>
                    {searchTerm ?
                      'No resources match your search' :
                      `No ${resourceType === 'subjectFile' ? 'subject files' : resourceType === 'ltsm' ? 'LTSM resources' : 'forms'} found`}
                  </Typography>
                  <Box display="flex" justifyContent="center" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={handleUploadDialogOpen}
                      sx={{
                        borderColor: THEME_COLORS.primary,
                        color: THEME_COLORS.primary,
                        '&:hover': {
                          borderColor: '#1e1c4e',
                          backgroundColor: 'rgba(39, 35, 97, 0.04)'
                        }
                      }}
                    >
                      Upload File
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<LinkIcon />}
                      onClick={handleAddLinkDialogOpen}
                      sx={{
                        backgroundColor: THEME_COLORS.primary,
                        '&:hover': { backgroundColor: '#1e1c4e' }
                      }}
                    >
                      Add Link
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <List>
                    {filteredResources.map((resource) => (
                      <Paper
                        key={resource.id}
                        elevation={1}
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: 3
                          }
                        }}
                      >
                        <ListItem
                          sx={{
                            borderRadius: 2,
                            py: 1.5
                          }}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={(e) => handleMenuClick(e, resource.id)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          }
                        >
                          <ListItemIcon>
                            {getFileIcon(resource.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography fontWeight="medium">
                                {resource.name}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                {resource.description && (
                                  <Typography variant="body2" color="text.secondary">
                                    {resource.description}
                                  </Typography>
                                )}
                                <Box display="flex" gap={2} mt={0.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(resource.uploadedAt).toLocaleDateString()}
                                  </Typography>
                                  {resource.size && (
                                    <Typography variant="caption" color="text.secondary">
                                      {resource.size}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                                    {resource.type}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                      </Paper>
                    ))}
                  </List>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
     
        {/* Subject Selection */}
        <Grid item sx={{ width: { xs: '100%', md: '33%' } }}>
          <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                My Subjects
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <List disablePadding>
                {subjects.map((subject) => (
                  <ListItem key={subject.id} disablePadding sx={{ mb: 1 }}>
                    <Button
                      fullWidth
                      variant={selectedSubject?.id === subject.id ? "contained" : "outlined"}
                      onClick={() => handleSubjectChange(subject)}
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        textTransform: "none",
                        pl: 2,
                        py: 1.5,
                        borderRadius: 1,
                        bgcolor: selectedSubject?.id === subject.id ? THEME_COLORS.primary : 'transparent',
                        color: selectedSubject?.id === subject.id ? 'white' : 'text.primary',
                        borderColor: selectedSubject?.id === subject.id ? THEME_COLORS.primary : 'divider',
                        '&:hover': {
                          bgcolor: selectedSubject?.id === subject.id ? '#1e1c4e' : 'rgba(39, 35, 97, 0.04)',
                          borderColor: selectedSubject?.id === subject.id ? '#1e1c4e' : THEME_COLORS.primary,
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" width="100%">
                        <FolderIcon
                          sx={{
                            color: selectedSubject?.id === subject.id ? '#ed1566' : THEME_COLORS.primary,
                            mr: 1.5
                          }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight="medium" color="inherit">
                            {subject.name}
                          </Typography>
                          <Typography variant="caption" color={selectedSubject?.id === subject.id ? 'inherit' : 'text.secondary'}>
                            {subject.code ? `${subject.code} • ` : ''}Grade {subject.grade}
                          </Typography>
                        </Box>
                      </Box>
                    </Button>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Resource Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
          const resource = resources.find(r => r.id === activeResourceId);
          if (resource) window.open(resource.url, '_blank');
          handleMenuClose();
        }}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download/Open</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteResource}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: '#f44336' }}>Delete Resource</ListItemText>
        </MenuItem>
      </Menu>

      {/* Upload Dialog */}
      <Dialog
        open={isUploadDialogOpen}
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Resource</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ my: 2 }}>
            <Box mb={2}>
              <Typography variant="subtitle1" gutterBottom>
                Resource Type: {resourceType === 'subjectFile' ? 'Subject Files' : 
                               resourceType === 'ltsm' ? 'Learning & Teaching Support Material' : 'Forms'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select a file to upload to {selectedSubject?.name} resources.
              </Typography>
            </Box>
            <input
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              id="resource-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="resource-file">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                sx={{
                  borderColor: THEME_COLORS.primary,
                  color: THEME_COLORS.primary,
                  '&:hover': {
                    borderColor: THEME_COLORS.primary,
                    bgcolor: 'rgba(39, 35, 97, 0.04)'
                  }
                }}
              >
                Choose File
              </Button>
            </label>
            {selectedFile && (
              <Box mt={3}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 1, display: 'flex', alignItems: 'center' }}
                >
                  {getFileIcon(selectedFile.name.split('.').pop())}
                  <Box ml={2}>
                    <Typography variant="body2" fontWeight="medium">
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                </Paper>
                <TextField
                  margin="normal"
                  fullWidth
                  label="Description (Optional)"
                  variant="outlined"
                  multiline
                  rows={2}
                  placeholder="Add a description for this resource"
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose}>Cancel</Button>
          <Button 
            onClick={handleUploadResource} 
            variant="contained"
            disabled={!selectedFile}
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': {
                bgcolor: '#1e1c4e'
              }
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Link Dialog */}
      <Dialog
        open={isAddLinkDialogOpen}
        onClose={handleAddLinkDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Resource Link</DialogTitle>
        <DialogContent dividers>
          <Box my={1}>
            <TextField
              autoFocus
              margin="normal"
              label="Resource Name"
              name="name"
              value={newResourceData.name}
              onChange={handleNewResourceChange}
              fullWidth
              required
            />
            <TextField
              margin="normal"
              label="Resource URL"
              name="link"
              value={newResourceData.link}
              onChange={handleNewResourceChange}
              fullWidth
              required
              placeholder="https://"
            />
            <TextField
              margin="normal"
              label="Description (Optional)"
              name="description"
              value={newResourceData.description}
              onChange={handleNewResourceChange}
              fullWidth
              multiline
              rows={2}
              placeholder="Add a description for this resource"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddLinkDialogClose}>Cancel</Button>
          <Button 
            onClick={handleAddLink} 
            variant="contained"
            disabled={!newResourceData.name || !newResourceData.link}
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': {
                bgcolor: '#1e1c4e'
              }
            }}
          >
            Add Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubjectResources;